import React from 'react';
import SideBarContainer from 'Containers/SideBarContainer';
import Title from 'Components/Title';
import Alert from 'Components/Alert';
import type { AdminCourseCreateI } from 'types';
import useCoursesPage from './Hooks/useCoursesPage';
import useModal from './Hooks/useModal';
import FiltersBar from './Components/molecules/FiltersBar/FiltersBar';
import CoursesCardGrid from './Components/organisms/CoursesCardGrid/CoursesCardGrid';
import CoursesTable from './Components/organisms/CoursesTable/CoursesTable';
import { ErrorState, RetryButton } from './Courses.styled';

// useModal es JS legacy: TS infiere `onAccept?: () => void` desde su default y
// rechazaría el callback con parámetro. El contrato real del modal recibe el
// payload de alta (AddCourseModal.handleAccept).
interface UseModalI {
  setOpenModal: (open: boolean) => void;
  AddCourseModal: (props: { acceptText?: string; onAccept?: (course: AdminCourseCreateI) => void }) => React.ReactElement;
}

export default function Courses() {
  const {
    t2,
    loading,
    error,
    retry,
    courses,
    cardSections,
    categories,
    authors,
    terminals,
    statusOptions,
    colorCategory,
    isCard,
    setIsCard,
    setName,
    setCategory,
    setAuthor,
    setTerminal,
    setStatusName,
    goToCourseDetails,
    goToEditCourse,
    handleCreateCourse,
    successful,
    open,
    setOpen,
    alertMsg,
  } = useCoursesPage();

  const { setOpenModal, AddCourseModal } = useModal() as unknown as UseModalI;

  let content;
  if (error) {
    content = (
      <ErrorState>
        {t2('LoadError')}
        <RetryButton type="button" onClick={retry}>
          {t2('Retry')}
        </RetryButton>
      </ErrorState>
    );
  } else if (isCard) {
    content = (
      <CoursesCardGrid
        sections={cardSections}
        loading={loading}
        emptyText={t2('NoData')}
        onCourseClick={goToCourseDetails}
        onCourseEdit={goToEditCourse}
      />
    );
  } else {
    content = (
      <CoursesTable
        courses={courses}
        colorCategory={colorCategory}
        headers={
          [t2('TableTitle'), t2('Description'), t2('Priority'), t2('Category'), t2('CreatedBy')] as string[]
        }
        loading={loading}
        onCourseClick={goToCourseDetails}
      />
    );
  }

  return (
    <SideBarContainer>
      <Title
        title={t2('Title')}
        textButton={t2('AddCourse')}
        disabledButton={false}
        eventButton={async () => setOpenModal(true)}
      />
      <FiltersBar
        searchPlaceholder={t2('Search') as string}
        statusTitle={t2('Status') as string}
        authorTitle={t2('Author') as string}
        categoryTitle={t2('Category') as string}
        terminalTitle={t2('Terminal') as string}
        statusOptions={statusOptions}
        authors={authors}
        categories={categories}
        terminals={terminals}
        isCard={isCard}
        onNameChange={setName}
        onStatusChange={setStatusName}
        onAuthorChange={setAuthor}
        onCategoryChange={setCategory}
        onTerminalChange={setTerminal}
        onToggleView={setIsCard}
      />
      {content}
      <AddCourseModal acceptText={t2('CreateCourse') as string} onAccept={handleCreateCourse} />
      <Alert message={alertMsg} open={open} setOpen={setOpen} error={!successful} />
    </SideBarContainer>
  );
}
